import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { RealtimeClient, type RealtimeClientOptions } from "@cntrl/realtime";
import { onNativeResume } from "@cntrl/native";
import type { ApiClient } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { HUB_URL } from "../../config/env.js";
import { mintRealtimeTicket } from "./ticket.js";
import { applySignal, refetchAllScopes } from "./invalidationRegistry.js";

/** The realtime surface exposed to the authed app — declarative group join/leave over the one connection. */
export interface RealtimeContextValue {
  join(topic: string): Promise<void>;
  leave(topic: string): Promise<void>;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

/**
 * Coalescing window for the foreground resync. On an iOS foreground BOTH the native
 * `appStateChange→active` and the webview `visibilitychange→visible` fire within a few ms of each other
 * (a push tap that foregrounds the app adds a third trigger); this leading-edge window collapses the burst
 * to ONE `reconnectNow` — "one refetch, not two". Long enough to swallow the double-fire,
 * short enough to be imperceptible.
 */
const FOREGROUND_COALESCE_MS = 300;

/**
 * Resync when the app returns to the foreground. iOS suspends the webview in the background so the
 * socket dies; on foreground we do two things, coalesced into one per foreground burst: (1) invalidate every
 * subscribed scope so a REST refetch pulls current truth immediately (the user-facing catch-up — this is what
 * makes the hook effective, since `withAutomaticReconnect` keeps a dropped socket *Reconnecting*, not
 * *Disconnected*, so reconnectNow alone is usually a no-op); (2) {@link RealtimeClient.reconnectNow} to
 * force-start a fully *Disconnected* socket (the edge case) — a Reconnecting socket is left to SignalR's own
 * backoff, which fires `onreconnected`→refetch when JS resumes. Triggers: DOM `visibilitychange`→visible,
 * bfcache `pageshow` (persisted), and native `appStateChange`→active (through the @cntrl/native seam; no-op on
 * web). All funnel through one leading-edge coalescer (the iOS appStateChange+visibilitychange double-fire and
 * a push-tap foreground collapse to one refetch, not two).
 */
function useForegroundResync(client: RealtimeClient, queryClient: QueryClient): void {
  useEffect(() => {
    let cooling = false;
    let coolTimer: ReturnType<typeof setTimeout> | null = null;

    const resync = () => {
      if (cooling) return; // inside the coalescing window — collapse into the leading call
      cooling = true;
      coolTimer = setTimeout(() => {
        cooling = false;
        coolTimer = null;
      }, FOREGROUND_COALESCE_MS);
      void client.reconnectNow();
      refetchAllScopes(queryClient); // "invalidate subscribed scopes" — immediate REST truth on foreground
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") resync();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) resync(); // restored from the bfcache — the socket is gone
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    const removeNativeResume = onNativeResume(resync);

    return () => {
      if (coolTimer) clearTimeout(coolTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      removeNativeResume();
    };
  }, [client, queryClient]);
}

/**
 * The production wiring of the one hub connection to the app, extracted so it is directly
 * testable independent of the client transport: `mintTicket` mints a WS ticket over authenticated REST;
 * `onConnected` (fired on first connect AND every reconnect) runs the GATE-4 unconditional REST refetch of
 * every subscribed scope; `onSignal` runs the event→invalidation registry.
 */
export function buildRealtimeOptions(queryClient: QueryClient, api: ApiClient): RealtimeClientOptions {
  return {
    hubUrl: HUB_URL,
    mintTicket: () => mintRealtimeTicket(api),
    onConnected: () => refetchAllScopes(queryClient),
    onSignal: (signal) => applySignal(queryClient, signal),
  };
}

export interface RealtimeProviderProps {
  children: ReactNode;
  /** Inject a client in tests; production constructs the real one from the app services. */
  client?: RealtimeClient;
}

/**
 * Owns the ONE SignalR hub connection for the authed app. Mounted inside the authenticated shell
 * layout, so the connection's life is exactly the authenticated session: it starts when the shell mounts (a
 * valid bearer exists to mint the ticket) and stops when the shell unmounts (logout / redirect to /login).
 * The connection is PERSON-bound (the ticket authenticates the person), so it survives a band switch
 * — only band-scoped group membership changes, driven declaratively by {@link useLiveGroup}.
 *
 * Signal wiring (the two callbacks the client fires): `onConnected` on every (re)connect → the GATE-4
 * unconditional REST refetch of all subscribed scopes; `onSignal` → the event→invalidation registry.
 */
export function RealtimeProvider({ children, client: injected }: RealtimeProviderProps) {
  const { api } = useApp();
  const queryClient = useQueryClient();

  // One client for the provider's life. The handlers close over the STABLE queryClient/api (both singletons),
  // so the client is built once — never rebuilt on render, which would drop the connection.
  const clientRef = useRef<RealtimeClient | null>(null);
  if (clientRef.current === null) {
    clientRef.current = injected ?? new RealtimeClient(buildRealtimeOptions(queryClient, api));
  }
  const client = clientRef.current;

  useEffect(() => {
    // Best-effort: realtime is an enhancement over REST, so a failed handshake must never break the shell —
    // the client's own backoff retries, and REST reads still work. Stop on unmount (logout tears the socket down).
    void client.start().catch(() => {});
    return () => {
      void client.stop();
    };
  }, [client]);

  // Foreground resync: refetch subscribed scopes + reconnect a dead socket when the app returns to
  // the foreground. Its listener lifetime is exactly the connection's (mounted here, torn down on unmount/logout).
  useForegroundResync(client, queryClient);

  const value = useMemo<RealtimeContextValue>(
    () => ({
      join: (topic) => client.joinGroup(topic),
      leave: (topic) => client.leaveGroup(topic),
    }),
    [client],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/** The realtime surface. Returns null outside a provider (e.g. a shell rendered in isolation in a test). */
export function useRealtimeOrNull(): RealtimeContextValue | null {
  return useContext(RealtimeContext);
}

/**
 * Declaratively subscribe to a group for the lifetime of the mounting component (router-driven
 * join/leave): join on mount, leave on unmount, re-subscribe when the topic changes (e.g. a band switch
 * changes `band:{id}`). A null topic is a no-op. Outside a {@link RealtimeProvider} it is a no-op too, so a
 * component works with or without live updates (REST is always the source of truth).
 */
export function useLiveGroup(topic: string | null): void {
  const realtime = useRealtimeOrNull();
  useEffect(() => {
    if (!realtime || !topic) return;
    void realtime.join(topic);
    return () => {
      void realtime.leave(topic);
    };
  }, [realtime, topic]);
}
